/*
 * Branded Account Statement PDF (Feb-18).
 * Amber-themed letterhead + party details + ledger table + running balance card.
 */
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const THEME = {
  primary: [240, 180, 60],
  primaryDark: [200, 140, 40],
  dark: [15, 23, 42],
  body: [51, 65, 85],
  muted: [100, 116, 139],
  border: [226, 232, 240],
  bandBg: [254, 243, 199],
  rose: [225, 29, 72],
  emerald: [5, 150, 105],
  sky: [2, 132, 199],
};

const sanitize = (t) => t == null ? '' : String(t)
  .replace(/[\u2018\u2019]/g, "'")
  .replace(/[\u201C\u201D]/g, '"')
  .replace(/[\u2013\u2014]/g, '-')
  .replace(/[^\x00-\x7E]/g, '?');

function drawLetterhead(doc, company) {
  const W = doc.internal.pageSize.getWidth();
  doc.setFillColor(...THEME.primary);
  doc.rect(0, 0, W, 90, 'F');
  doc.setTextColor(...THEME.dark);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text(sanitize(company?.name || 'FM Flexi Pro'), 32, 46);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(sanitize('by SGK Apps - Facilities Management Suite'), 32, 62);
  doc.setFontSize(9);
  if (company?.tax_registration_number) {
    doc.text(sanitize(`TRN: ${company.tax_registration_number}`), W - 32, 46, { align: 'right' });
  }
  if (company?.email) doc.text(sanitize(company.email), W - 32, 62, { align: 'right' });
}

function drawFooter(doc, pageIndex, pageTotal) {
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  doc.setFillColor(...THEME.primary);
  doc.rect(0, H - 48, W, 4, 'F');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...THEME.muted);
  doc.text('FM Flexi Pro (TM) - Statement of Account', W / 2, H - 34, { align: 'center' });
  doc.text(`Page ${pageIndex} of ${pageTotal}`, W - 32, H - 34, { align: 'right' });
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(7.5);
  doc.setTextColor(...THEME.primaryDark);
  doc.text('SGK Apps, branding and a Product of VRV Technical & Technology Solutions FZ LLC',
    W / 2, H - 18, { align: 'center' });
}

export function generateStatementPDF({ statement, company, dateFrom, dateTo, view = false }) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();

  drawLetterhead(doc, company);
  let y = 130;

  doc.setTextColor(...THEME.dark);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text('STATEMENT OF ACCOUNT', 32, y);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...THEME.muted);
  doc.text(sanitize(`Generated ${new Date().toLocaleString()}`), W - 32, 130, { align: 'right' });

  y += 20;
  doc.setDrawColor(...THEME.primary);
  doc.setLineWidth(2);
  doc.line(32, y, 80, y);
  y += 22;

  // Party card
  doc.setFillColor(...THEME.bandBg);
  doc.setDrawColor(...THEME.primaryDark);
  doc.setLineWidth(0.6);
  doc.roundedRect(32, y, W - 64, 78, 6, 6, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...THEME.primaryDark);
  doc.text(sanitize(statement.party_type.toUpperCase()), 44, y + 20);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(...THEME.dark);
  doc.text(sanitize(statement.party_name), 44, y + 42);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...THEME.body);
  const period = (dateFrom || dateTo)
    ? `Period: ${dateFrom || 'inception'} to ${dateTo || 'today'}`
    : 'Period: Full ledger';
  doc.text(sanitize(period), 44, y + 62);

  // Closing balance callout — right side
  const balance = Number(statement.totals.balance || 0);
  const balColor = balance > 0 ? THEME.rose : balance < 0 ? THEME.emerald : THEME.muted;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...balColor);
  doc.text('CLOSING BALANCE', W - 44, y + 20, { align: 'right' });
  doc.setFontSize(22);
  doc.setTextColor(...THEME.dark);
  doc.text(sanitize(`${statement.currency} ${Math.abs(balance).toLocaleString()}`), W - 44, y + 46, { align: 'right' });
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...THEME.body);
  const label = balance > 0
    ? (statement.party_type === 'supplier' ? 'Payable to supplier' : `Receivable from ${statement.party_type}`)
    : balance < 0
      ? (statement.party_type === 'supplier' ? 'Advance / credit' : `Credit owed to ${statement.party_type}`)
      : 'Settled';
  doc.text(sanitize(label), W - 44, y + 62, { align: 'right' });

  y += 94;

  const entries = statement.entries || [];
  if (entries.length === 0) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(11);
    doc.setTextColor(...THEME.muted);
    doc.text('No activity for this party in the selected period.', 32, y);
  } else {
    autoTable(doc, {
      startY: y,
      head: [['Date', 'Type', 'Reference', 'Description', `Debit`, `Credit`, `Balance`]],
      body: entries.map(e => [
        sanitize(e.date || ''),
        sanitize(e.type || ''),
        sanitize(e.reference || ''),
        sanitize((e.description || '').slice(0, 80)),
        e.debit ? Number(e.debit).toLocaleString() : '-',
        e.credit ? Number(e.credit).toLocaleString() : '-',
        Number(e.balance).toLocaleString(),
      ]),
      foot: [[
        { content: 'TOTALS', colSpan: 4, styles: { halign: 'right', fontStyle: 'bold' } },
        { content: Number(statement.totals.debit).toLocaleString(), styles: { fontStyle: 'bold' } },
        { content: Number(statement.totals.credit).toLocaleString(), styles: { fontStyle: 'bold' } },
        { content: `${statement.currency} ${Number(statement.totals.balance).toLocaleString()}`, styles: { fontStyle: 'bold', textColor: THEME.primaryDark } },
      ]],
      styles: { font: 'helvetica', fontSize: 8.5, cellPadding: 5, textColor: THEME.body, lineColor: THEME.border, lineWidth: 0.4 },
      headStyles: { fillColor: THEME.primary, textColor: 255, fontStyle: 'bold' },
      footStyles: { fillColor: [254, 243, 199], textColor: THEME.dark },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 80 },
        2: { cellWidth: 80, font: 'courier' },
        4: { halign: 'right', cellWidth: 60 },
        5: { halign: 'right', cellWidth: 60 },
        6: { halign: 'right', cellWidth: 70, fontStyle: 'bold' },
      },
      margin: { left: 32, right: 32 },
      didDrawPage: () => { drawLetterhead(doc, company); },
    });
  }

  // Footer on all pages
  const total = doc.internal.pages.length - 1;
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    drawFooter(doc, i, total);
  }

  const safeName = `Statement_${sanitize(statement.party_name).replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
  if (view) {
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  } else {
    doc.save(safeName);
  }
}
